import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Bookmark, MapPin, Box, Calendar, AlignLeft, Image, ImagePlus } from 'lucide-react';
import { QuotationBlock as QuotationBlockComponent } from './QuotationBlock';
import { AccommodationForm } from './AccommodationForm';
import { TourForm } from './TourForm';
import { RestaurantForm } from './RestaurantForm';
import { TransportationForm } from './TransportationForm';
import { itemToLibraryItem, extractLocationData } from '@/lib/library-utils';
import { LibraryService } from '@/lib/library-service';
import { 
  QuotationBlock, 
  AccommodationInfo, 
  TourInfo, 
  RestaurantInfo, 
  TransportationInfo,
  LibraryItem
} from './types';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { PlacesAutocompleteV1 } from '@/components/ui/places-autocomplete-v1';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { ImageManagerModal } from '@/components/ui/image-manager-modal';
import { formatSafeDate } from '@/lib/date-utils';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

type ItemType = 'block' | 'accommodation' | 'tour' | 'restaurant' | 'transportation';

interface CreateItemFormProps {
  isOpen: boolean;
  itemType: ItemType;
  onClose: () => void;
  onItemCreated: (libraryItem: LibraryItem) => void;
  existingItem?: LibraryItem;
  isEditMode?: boolean;
}

// Simple mock components for the forms with a consistent interface
const SimplifiedAccommodationForm = ({ accommodation, onChange }: { accommodation: AccommodationInfo, onChange: (data: AccommodationInfo) => void }) => {
  return (
    <div className="p-4 border rounded bg-gray-50">
      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-500">Campos simulados - versão simplificada para demonstração</h4>
      </div>
      <AccommodationForm 
        accommodation={accommodation} 
        index={0}
        pageId="temp"
        updateAccommodation={(pageId, id, field, value) => {
          onChange({ ...accommodation, [field]: value });
        }}
        batchUpdateAccommodation={(id, updates) => {
           onChange({ ...accommodation, ...updates });
        }}
        deleteAccommodation={() => {}}
        isCollapsed={false}
        onToggleCollapse={() => {}}
      />
    </div>
  );
};

const SimplifiedTourForm = ({ tour, onChange }: { tour: TourInfo, onChange: (data: TourInfo) => void }) => {
  return (
    <div className="p-4 border rounded bg-gray-50">
      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-500">Campos simulados - versão simplificada para demonstração</h4>
      </div>
      <TourForm 
        tour={tour} 
        index={0}
        pageId="temp"
        updateTour={(pageId, id, field, value) => {
          onChange({ ...tour, [field]: value });
        }}
        batchUpdateTour={(id, updates) => {
           onChange({ ...tour, ...updates });
        }}
        deleteTour={() => {}}
        isCollapsed={false}
        onToggleCollapse={() => {}}
      />
    </div>
  );
};

const SimplifiedRestaurantForm = ({ restaurant, onChange }: { restaurant: RestaurantInfo, onChange: (data: RestaurantInfo) => void }) => {
  return (
    <div className="p-4 border rounded bg-gray-50">
      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-500">Campos simulados - versão simplificada para demonstração</h4>
      </div>
      <RestaurantForm 
        restaurant={restaurant} 
        index={0}
        pageId="temp"
        updateRestaurant={(pageId, id, field, value) => {
          onChange({ ...restaurant, [field]: value });
        }}
        batchUpdateRestaurant={(id, updates) => {
           onChange({ ...restaurant, ...updates });
        }}
        deleteRestaurant={() => {}}
        isCollapsed={false}
        onToggleCollapse={() => {}}
      />
    </div>
  );
};

const SimplifiedTransportationForm = ({ transportation, onChange }: { transportation: TransportationInfo, onChange: (data: TransportationInfo) => void }) => {
  return (
    <div className="p-4 border rounded bg-gray-50">
      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-500">Campos simulados - versão simplificada para demonstração</h4>
      </div>
      <TransportationForm 
        transportation={transportation} 
        index={0}
        pageId="temp"
        updateTransportation={(pageId, id, field, value) => {
          onChange({ ...transportation, [field]: value });
        }}
        batchUpdateTransportation={(id, updates) => {
           onChange({ ...transportation, ...updates });
        }}
        deleteTransportation={() => {}}
        isCollapsed={false}
        onToggleCollapse={() => {}}
      />
    </div>
  );
};

// Styled components for the block form
const StyledLabel = ({ 
  children, 
  className, 
  icon: Icon = MapPin 
}: { 
  children: React.ReactNode; 
  className?: string; 
  icon?: React.ElementType
}) => (
  <div className={cn("flex items-center gap-1", className)}>
    <Icon className="h-3.5 w-3.5 text-gray-500" />
    <Label className="text-gray-500 font-medium uppercase tracking-wide">{children}</Label>
  </div>
);

const FormInput = ({ className, ...props }: React.ComponentProps<typeof Input>) => (
  <Input
    className={cn("text-sm", className)}
    {...props}
  />
);

// Simplified version of QuotationBlock that uses a custom implementation
const SimplifiedBlockForm = ({ block, onChange }: { block: QuotationBlock, onChange: (data: QuotationBlock) => void }) => {
  const { toast } = useToast();
  const [isImageManagerOpen, setIsImageManagerOpen] = useState(false);
  const [isProcessingDescription, setIsProcessingDescription] = useState(false);

  // Handle place select from Places Autocomplete
  const handlePlaceSelect = (placeData: {
    placeId: string;
    name: string;
    address: string;
    coordinates: { lat: number; lng: number };
    googleMapsUrl: string;
    city: string;
    country: string;
  }) => {
    // Update multiple fields at once
    const updates: Partial<QuotationBlock> = {
      placeId: placeData.placeId,
      location: placeData.name,
      coordinates: placeData.coordinates ? {
        lat: Number(placeData.coordinates.lat),
        lng: Number(placeData.coordinates.lng)
      } : undefined,
      googleMapsUrl: placeData.googleMapsUrl,
      city: placeData.city,
      country: placeData.country
    };
    
    // Update the block with all location data
    onChange({
      ...block,
      ...updates
    });
    
    // Show toast confirmation
    toast({
      title: "Local adicionado",
      description: `${placeData.name} foi vinculado a este bloco.`,
    });
  };

  // Update images when selected from the image manager
  const handleUpdateImages = (images: string[]) => {
    onChange({
      ...block,
      images
    });
  };

  // Listen for AI processing events
  useEffect(() => {
    const handleProcessingState = (event: CustomEvent<{pageId: string; blockId: string; isProcessing: boolean}>) => {
      const { blockId, isProcessing } = event.detail;
      if (blockId === block.id) {
        setIsProcessingDescription(isProcessing);
      }
    };
    
    document.addEventListener('block-description-processing', handleProcessingState as EventListener);
    
    return () => {
      document.removeEventListener('block-description-processing', handleProcessingState as EventListener);
    };
  }, [block.id]);

  return (
    <Card className="border-l-4 border-l-voah-teal">
      <CardContent className="p-5 space-y-4">
        {/* Title */}
        <div className="space-y-1">
          <StyledLabel className="text-xs" icon={Box}>Título</StyledLabel>
          <FormInput
            value={block.title || ''}
            onChange={(e) => onChange({ ...block, title: e.target.value })}
            placeholder="Título do bloco"
            className="h-10 text-base font-medium"
          />
        </div>

        {/* Location */}
        <div className="space-y-1">
          <StyledLabel className="text-xs" icon={MapPin}>Localização</StyledLabel>
          <div className="flex items-center gap-2 w-full">
            <MapPin className="h-4 w-4 text-gray-500 flex-shrink-0" />
            <div className="flex-grow w-full">
              <PlacesAutocompleteV1
                value={block.location || ''}
                onChange={(value) => onChange({ ...block, location: value })}
                onPlaceSelect={handlePlaceSelect}
                placeholder="Digite um local"
                className="w-full"
                showNameInsteadOfAddress={true}
              />
            </div>
          </div>
          {block.googleMapsUrl && (
            <div className="text-xs text-right mt-1">
              <a 
                href={block.googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-voah-teal hover:underline"
              >
                Ver no Google Maps
              </a>
            </div>
          )}
        </div>

        {/* Dates */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1">
            <StyledLabel className="text-xs" icon={Calendar}>Data de início</StyledLabel>
            <div className="flex items-center">
              <Calendar className="h-4 w-4 mr-2 text-gray-500" />
              <FormInput
                type="date"
                value={block.startDate || ''}
                onChange={(e) => onChange({ ...block, startDate: e.target.value })}
                className="h-8 text-sm"
              />
            </div>
          </div>
          <div className="space-y-1">
            <StyledLabel className="text-xs" icon={Calendar}>Data de fim</StyledLabel>
            <div className="flex items-center">
              <Calendar className="h-4 w-4 mr-2 text-gray-500" />
              <FormInput
                type="date"
                value={block.endDate || ''}
                onChange={(e) => onChange({ ...block, endDate: e.target.value })}
                className="h-8 text-sm"
              />
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="space-y-1">
          <StyledLabel className="text-xs" icon={AlignLeft}>Descrição</StyledLabel>
          <RichTextEditor
            value={block.description || ''}
            onChange={(value) => onChange({ ...block, description: value })}
            placeholder="Descreva este bloco de cotação..."
            rows={4}
            onProcessWithAI={() => {
              document.dispatchEvent(new CustomEvent('process-block-description', {
                detail: {
                  pageId: 'temp',
                  blockId: block.id
                }
              }));
            }}
            isProcessing={isProcessingDescription}
          />
        </div>

        {/* Images */}
        <div className="space-y-1">
          <StyledLabel className="text-xs" icon={Image}>Imagens</StyledLabel>
          <div className="flex flex-wrap gap-2">
            {block.images && block.images.length > 0 ? (
              block.images.map((image, idx) => (
                <div key={idx} className="relative group h-16 w-16">
                  <img 
                    src={image} 
                    alt={`Imagem ${idx + 1}`} 
                    className="h-full w-full object-cover rounded-md border border-gray-200"
                  />
                </div>
              ))
            ) : (
              <div className="text-sm text-gray-500">Nenhuma imagem adicionada</div>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsImageManagerOpen(true)}
              className="h-16 w-16 flex flex-col items-center justify-center"
            >
              <ImagePlus className="h-5 w-5 mb-1" />
              <span className="text-xs">Adicionar</span>
            </Button>
          </div>
        </div>
      </CardContent>

      {/* Image Manager Modal */}
      {isImageManagerOpen && (
        <ImageManagerModal
          isOpen={isImageManagerOpen}
          onClose={() => setIsImageManagerOpen(false)}
          images={block.images || []}
          onImagesChange={handleUpdateImages}
          contextId={block.id}
          contextType={itemType === 'block' ? 'customSection' : itemType}
        />
      )}
    </Card>
  );
};

export function CreateItemForm({
  isOpen,
  itemType,
  onClose,
  onItemCreated,
  existingItem,
  isEditMode = false
}: CreateItemFormProps) {
  // Estado inicial baseado no tipo de item ou item existente
  const [formData, setFormData] = useState(() => {
    const id = uuidv4();
    const timestamp = Date.now();
    
    // Se estivermos no modo de edição e temos um item existente, use seus dados
    if (isEditMode && existingItem) {
      // Convert date objects to strings for QuotationBlock items
      if (itemType === 'block') {
        const blockData = existingItem.data as QuotationBlock;
        return {
          ...blockData,
          startDate: blockData.startDate && Object.prototype.toString.call(blockData.startDate) === '[object Date]' 
            ? formatSafeDate(blockData.startDate, 'yyyy-MM-dd') 
            : blockData.startDate,
          endDate: blockData.endDate && Object.prototype.toString.call(blockData.endDate) === '[object Date]' 
            ? formatSafeDate(blockData.endDate, 'yyyy-MM-dd') 
            : blockData.endDate
        };
      }
      return existingItem.data;
    }
    
    // Criar estado inicial baseado no tipo
    switch (itemType) {
      case 'block':
        return {
          id: `block_${timestamp}`,
          title: '',
          location: '',
          description: '',
          notes: '',
          images: [],
          city: '',
          country: ''
        } as QuotationBlock;
      
      case 'accommodation':
        return {
          id: `accommodation_${timestamp}`,
          name: '',
          location: '',
          description: '',
          notes: '',
          images: [],
          city: '',
          country: ''
        } as AccommodationInfo;
      
      case 'tour':
        return {
          id: `tour_${timestamp}`,
          name: '',
          location: '',
          description: '',
          notes: '',
          images: [],
          city: '',
          country: ''
        } as TourInfo;
      
      case 'restaurant':
        return {
          id: `restaurant_${timestamp}`,
          name: '',
          location: '',
          description: '',
          notes: '',
          images: [],
          city: '',
          country: ''
        } as RestaurantInfo;
      
      case 'transportation':
        return {
          id: `transportation_${timestamp}`,
          name: '',
          location: '',
          description: '',
          notes: '',
          images: [],
          currency: 'BRL',
          date: '',
          time: '',
          duration: '',
          globalOrder: 0,
          city: '',
          country: ''
        } as TransportationInfo;
      
      default:
        return {
          id: '',
          city: '',
          country: ''
        };
    }
  });

  // Verificar se o formulário é válido para salvar
  const isFormValid = () => {
    switch (itemType) {
      case 'block':
        return !!(formData as QuotationBlock).title;
      case 'accommodation':
        return !!(formData as AccommodationInfo).name;
      case 'tour':
        return !!(formData as TourInfo).name;
      case 'restaurant':
        return !!(formData as RestaurantInfo).name;
      case 'transportation':
        return !!(formData as TransportationInfo).name;
      default:
        return false;
    }
  };

  // Handler para atualização de dados do formulário
  const handleFormChange = (data: any) => {
    setFormData(data);
  };

  // Salvar na biblioteca
  const handleSaveToLibrary = async () => {
    // Se estamos no modo de edição, preservamos a estrutura do item existente
    if (isEditMode && existingItem) {
      // Criar uma cópia atualizada do item existente com os novos dados
      
      // --- Corrected Logic ---
      // Extract location data from the CURRENT formData state
      const currentLocationData = extractLocationData(formData, itemType);
      
      // Construct the updated item
      const updatedItem: LibraryItem = {
        ...existingItem,
        data: formData, // Use current form state for data
        title: getItemTitle(),
        description: getItemDescription(),
        location: currentLocationData, // Use freshly extracted location data
        updatedAt: new Date()
      };
      
      // Notificar o componente pai
      onItemCreated(updatedItem);
      
      // Fechar o modal
      onClose();
      return;
    }
    
    // Para novos itens, usar o fluxo normal
    const libraryItem = itemToLibraryItem(formData, itemType, {
      title: getItemTitle(),
      description: getItemDescription(),
      tags: [],
      city: 'city' in formData ? (formData.city as string) : '',
      country: 'country' in formData ? (formData.country as string) : '',
      generateId: false // Don't generate ID, let Supabase handle it
    });
    
    try {
      // Salvar na biblioteca
      await LibraryService.saveItem(libraryItem);
      
      // Notificar o componente pai
      onItemCreated(libraryItem);
      
      // Fechar o modal
      onClose();
    } catch (error) {
      console.error('Erro ao salvar item na biblioteca:', error);
      // Você pode adicionar um toast ou alerta aqui se desejar
    }
  };

  // Obter título baseado no tipo de item
  const getItemTitle = (): string => {
    switch (itemType) {
      case 'block':
        return (formData as QuotationBlock).title || 'Novo Bloco';
      case 'accommodation':
        return (formData as AccommodationInfo).name || 'Hospedagem';
      case 'tour':
        return (formData as TourInfo).name || 'Passeio';
      case 'restaurant':
        return (formData as RestaurantInfo).name || 'Restaurante';
      case 'transportation':
        return (formData as TransportationInfo).name || 'Traslado';
      default:
        return 'Novo Item';
    }
  };

  // Obter descrição baseada no tipo de item
  const getItemDescription = (): string => {
    switch (itemType) {
      case 'block':
        return (formData as QuotationBlock).description || '';
      case 'accommodation':
        return (formData as AccommodationInfo).description || '';
      case 'tour':
        return (formData as TourInfo).description || '';
      case 'restaurant':
        return (formData as RestaurantInfo).description || '';
      case 'transportation':
        return (formData as TransportationInfo).description || '';
      default:
        return '';
    }
  };

  // Renderizar o formulário adequado
  const renderForm = () => {
    switch (itemType) {
      case 'block':
        return (
          <SimplifiedBlockForm 
            block={formData as QuotationBlock} 
            onChange={handleFormChange} 
          />
        );
      
      case 'accommodation':
        return (
          <SimplifiedAccommodationForm 
            accommodation={formData as AccommodationInfo} 
            onChange={handleFormChange} 
          />
        );
      
      case 'tour':
        return (
          <SimplifiedTourForm 
            tour={formData as TourInfo} 
            onChange={handleFormChange} 
          />
        );
      
      case 'restaurant':
        return (
          <SimplifiedRestaurantForm 
            restaurant={formData as RestaurantInfo} 
            onChange={handleFormChange} 
          />
        );
      
      case 'transportation':
        return (
          <SimplifiedTransportationForm 
            transportation={formData as TransportationInfo} 
            onChange={handleFormChange} 
          />
        );
      
      default:
        return <div>Tipo de formulário não suportado</div>;
    }
  };

  // Título do diálogo baseado no tipo e modo
  const getDialogTitle = () => {
    const prefix = isEditMode ? 'Editar' : 'Criar Novo';
    
    switch (itemType) {
      case 'block': return `${prefix} Bloco`;
      case 'accommodation': return `${prefix} Hospedagem`;
      case 'tour': return `${prefix} Tour`;
      case 'restaurant': return `${prefix} Restaurante`;
      case 'transportation': return `${prefix} Transporte`;
      default: return `${prefix} Item`;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{getDialogTitle()}</DialogTitle>
        </DialogHeader>
        
        <div className="py-4">
          {renderForm()}
          
          {/* Visual Debug - Remove after testing */}
          {/* <pre className="mt-4 p-2 bg-gray-100 border rounded text-xs overflow-auto max-h-48">
            {JSON.stringify(formData, null, 2)}
          </pre> */}
          {/* End Visual Debug */}
          
        </div>
        
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={onClose}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSaveToLibrary}
            disabled={!isFormValid()}
            className="bg-amber-500 hover:bg-amber-600 text-white"
          >
            <Bookmark className="mr-2 h-4 w-4" />
            {isEditMode ? 'Atualizar Item' : 'Salvar na Biblioteca'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 